/// <reference types="google.maps" />
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

/**
 * Google Maps JavaScript API のロードを一元化するファサード。
 *
 * - `@googlemaps/js-api-loader` の関数 API（setOptions / importLibrary）を薄くラップする。
 * - maps / marker ライブラリを一度だけロードし、以降は同じ Promise を返す（多重ロード防止）。
 * - client でのみ呼び出すこと（SSR では実行しない）。
 */

export interface GoogleMapsLibraries {
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
}

let librariesPromise: Promise<GoogleMapsLibraries> | null = null;

/**
 * maps / marker ライブラリをロードして返す。
 * 初回呼び出しで API キーを設定しロードを開始する。2回目以降は同じ Promise を返す。
 *
 * @param {string} apiKey - Google Maps JavaScript API のブラウザキー
 * @returns {Promise<GoogleMapsLibraries>} maps / marker ライブラリ
 */
export const loadGoogleMapsLibraries = (apiKey: string): Promise<GoogleMapsLibraries> => {
  if (librariesPromise) return librariesPromise;

  // 訪日外国人向けサービスのため地図ラベル（地名等）は英語で表示する。
  setOptions({ key: apiKey, v: 'weekly', language: 'en', region: 'JP' });

  // 失敗時はキャッシュ（reject 済み Promise）を残さずクリアし、次回呼び出しで再試行できるようにする。
  // 残すと一時的なネットワークエラーでもリロードするまでマップが永続的に使えなくなる。
  librariesPromise = Promise.all([importLibrary('maps'), importLibrary('marker')])
    .then(([maps, marker]) => ({ maps, marker }))
    .catch((error) => {
      librariesPromise = null;
      throw error;
    });

  return librariesPromise;
};
